import React from 'react';
import { Input, Select, Checkbox } from '@/components/ui';
import type { UserNewFormData } from '../types/userNew.types';

interface EmergencyStepProps {
  formData: UserNewFormData;
  updateField: (field: keyof UserNewFormData, value: any) => void;
}

const RELATIONSHIP_OPTIONS = [
  { value: 'spouse', label: 'Vợ / Chồng' },
  { value: 'parent', label: 'Bố / Mẹ' },
  { value: 'sibling', label: 'Anh / Chị / Em' },
  { value: 'child', label: 'Con' },
  { value: 'relative', label: 'Họ hàng' },
  { value: 'friend', label: 'Bạn bè' },
  { value: 'other', label: 'Khác' },
];

export function EmergencyStep({ formData, updateField }: EmergencyStepProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Cột trái – 2/3 */}
      <div className="lg:col-span-2 space-y-6">

        {/* Section: Người liên hệ khẩn cấp */}
        <div className="bg-white rounded-md p-6 border border-slate-100">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 rounded-full bg-rose-50 flex items-center justify-center">
              <i className="ri-phone-line text-rose-500 text-sm" />
            </div>
            <h2 className="sm:text-sm text-xs font-medium text-slate-800 tracking-wide">
              Người liên hệ khẩn cấp
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Họ và tên người liên hệ"
              placeholder="Nhập họ tên người liên hệ"
              value={formData.emergency_contact_name || ''}
              onChange={(e: any) => updateField('emergency_contact_name', e.target.value)}
            />

            <Input
              label="Số điện thoại"
              placeholder="Nhập số điện thoại liên hệ"
              value={formData.emergency_contact_phone || ''}
              onChange={(e: any) => updateField('emergency_contact_phone', e.target.value)}
            />

            <Select
              label="Mối quan hệ"
              value={formData.emergency_contact_relationship || ''}
              onValueChange={(val: string) => updateField('emergency_contact_relationship', val)}
              options={RELATIONSHIP_OPTIONS}
            />
          </div>
        </div>

        {/* Section: Xác nhận & Cài đặt tài khoản */}
        <div className="bg-white rounded-md p-6 border border-slate-100">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
              <i className="ri-shield-check-line text-emerald-600 text-sm" />
            </div>
            <h2 className="sm:text-sm text-xs font-medium text-slate-800 tracking-wide">
              Cài đặt bảo mật tài khoản
            </h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-slate-50/50 p-4 rounded-md border border-slate-100">
              <Checkbox
                checked={formData.require_password_change}
                onCheckedChange={(val: any) => updateField('require_password_change', val)}
              />
              <div>
                <p className="sm:text-sm text-xs text-slate-700 font-medium">
                  Yêu cầu đổi mật khẩu khi đăng nhập lần đầu
                </p>
                <p className="sm:text-sm text-xs text-slate-400 mt-0.5">
                  Nhân viên sẽ được nhắc đặt mật khẩu mới ngay sau khi đăng nhập thành công lần đầu.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section: Tóm tắt thông tin */}
        <div className="bg-slate-50 rounded-md p-6 border border-slate-100">
          <h2 className="sm:text-sm text-xs font-medium text-slate-600 mb-4 tracking-wide">
            Xác nhận thông tin trước khi tạo
          </h2>
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            <SummaryRow label="Họ và tên" value={formData.full_name} />
            <SummaryRow label="Email" value={formData.email} />
            <SummaryRow label="Số điện thoại" value={formData.phone} />
            <SummaryRow label="Giới tính" value={
              formData.gender === 'male' ? 'Nam' :
              formData.gender === 'female' ? 'Nữ' : 'Khác'
            } />
            <SummaryRow label="Loại nhân sự" value={
              formData.employment_type === 'full_time' ? 'Chính thức' :
              formData.employment_type === 'part_time' ? 'Bán thời gian' :
              formData.employment_type === 'contract' ? 'Hợp đồng' : 'Thử việc'
            } />
            <SummaryRow label="Vai trò hệ thống" value={formData.role_code?.toUpperCase() || 'PM'} />
            <SummaryRow label="Chức danh" value={formData.job_title} />
            <SummaryRow label="Ngày vào làm" value={formData.join_date} />
          </div>
        </div>
      </div>

      {/* Cột phải – 1/3 */}
      <div className="lg:col-span-1">
        <div className="bg-white rounded-md p-6 border border-slate-100 sticky top-6">
          <h2 className="sm:text-sm text-xs font-medium text-slate-800 mb-4 tracking-wide">
            Hướng dẫn
          </h2>
          <div className="space-y-4">
            <div className="p-3 rounded-md bg-amber-50 border border-amber-100">
              <p className="sm:text-sm text-xs text-amber-700 font-medium mb-1">
                <i className="ri-alert-line mr-1" />
                Kiểm tra kỹ trước khi hoàn tất
              </p>
              <p className="sm:text-sm text-xs text-amber-600">
                Sau khi nhấn Hoàn tất, mã nhân sự và tài khoản đăng nhập sẽ được tạo tự động và không thể hoàn tác dễ dàng.
              </p>
            </div>
            <div className="p-3 rounded-md bg-slate-50 border border-slate-100">
              <p className="sm:text-sm text-xs text-slate-600 font-medium mb-1">
                <i className="ri-mail-send-line mr-1" />
                Email kích hoạt
              </p>
              <p className="sm:text-sm text-xs text-slate-500">
                Hệ thống sẽ gửi email kèm mật khẩu tạm thời đến địa chỉ email đã đăng ký.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="sm:text-sm text-xs text-slate-400">{label}</span>
      <span className="sm:text-sm text-xs text-slate-700 font-medium">
        {value || <span className="text-slate-300 italic">Chưa nhập</span>}
      </span>
    </div>
  );
}
